// All or portions of this file Copyright © 2019 Amazon Web Services, Inc. or its affiliates or licensors.
// All rights reserved. For full license terms and conditions, please see the LICENSE file at the root of
// this distribution (the "License") or go to https://content.sumerian.amazonaws.com/engine/latest/license.txt.
// All use of the software is governed by the License. All materials are made available on an "AS IS" BASIS, WITHOUT
// WARRANTIES OR CONDITIONS OF ANY KIND, express or implied. Do not remove or modify any license notices.
import FadeTransitionState from '../../animationpack/state/FadeTransitionState';
import SyncFadeTransitionState from '../../animationpack/state/SyncFadeTransitionState';
import FrozenTransitionState from '../../animationpack/state/FrozenTransitionState';
import SteadyState from '../../animationpack/state/SteadyState';
import LayerLerpBlender from '../../animationpack/layer/LayerLerpBlender';
import World from '../../entities/World';
import MathUtils from '../../math/MathUtils';
import Transform from '../../math/Transform';

/**
 * Animation layers are essentially independent state machines, managed by a single AnimationManager. Each maintains a set of possible
 *        "steady states" - main states that the layer can be in. The layer can only be in one state at any given time. It may transition between
 *        states, provided that a path is defined for transition from the current state to the desired one. *
 */
class AnimationLayer {

  /**
   * @param {string} name Name of layer
   * @param {string} id Id of layer
   * @param {World} world world object that the layer is being added to
   */
  constructor(name, id, world) {
    this.id = id;
    this._name = name;

    this._steadyStates = {};
    this._currentState = null;
    this._layerBlender = new LayerLerpBlender();
    this._transitions = {};
    this._transitionStates = {};
    this._layerType = AnimationLayer.DEFAULT_LAYER_TYPE;
    this._additiveBaseInverseTransforms = {};
    this._additiveAnimationInitialized = false;

    if (world === undefined) {
      console.warn('[Sumerian] AnimationLayer object constructed without a world object. Playback may be erratic.');
    }
    this._world = world;
  }

  getWorldTime() {
    return this._world ? this._world.time : 0;
  }

  /**
   * Get available states for layer
   * @returns {Array<string>}
   */
  getStates() {
    return Object.keys(this._steadyStates);
  }

  /**
   * Add a state to the layer with the associated stateKey
   * @param {string} stateKey
   * @param {SteadyState} state
   */
  setState(stateKey, state) {
    this._steadyStates[stateKey] = state;
  }

  /**
   * Sets the blend weight of a layer. This will not affect the base
   * layer in the animation component.
   * @param {number} weight Should be between 0 and 1 and will be clamped
   */
  setBlendWeight(weight) {
    if (typeof(weight) !== "number" || Number.isNaN(weight)) {
      console.warn(`[Sumerian] Non-numeric blend weight (${weight}) specified for layer ${this._name}. setBlendWeight will have no effect.`)
      return;
    }

    if (this._layerBlender) {
      this._layerBlender._blendWeight = MathUtils.clamp(weight, 0, 1);
    }
  }

  /**
   * Get available transitions for current State
   * @returns {Array<string>}
   */
  getTransitions() {
    let transitions;
    if (this._currentState) {
      transitions = Object.keys(this._currentState._transitions);
    } else {
      transitions = [];
    }
    if (this._transitions) {
      for (const key in this._transitions) {
        if (transitions.indexOf(key) === -1) {
          transitions.push(key);
        }
      }
    }
    transitions.sort();
    return transitions;
  }

  /**
   * set the world object, called when containing Animation Component is attached or detached from an entity
   *
   * @param {?World} world world object the containing Animation Component entity belongs to, or null if the containing Animation Component is detached
   */
  set world(world) {
    this._world = world;
  }

  /**
   * Does the updating before animations are applied
   * @private
   */
  update(globalTime) {
    if (this._currentState) {
      this._currentState.update(typeof globalTime !== 'undefined' ? globalTime : this.getWorldTime());
    }
  }

  /**
   * Does the updating after animations are applied
   * @private
   */
  postUpdate() {
    if (this._currentState) {
      this._currentState.postUpdate();
    }
  }

  /**
   * Transition the layer to another state. The transition must be specified either on the state or on the layer (as a general transition), see FileFormat spec for more info.
   *
   * If the layer is currently transitioning, the transition is ignored unless doSwitchIfTransitiong is set to true, in which case the animState is immediately switched with no transition.
   *
   * @param {string} state the state Id to transition to
   * @param {number} [globalTime=this._world.time] start time for the transition, defaults to current time
   * @param {Function} [finishCallback] If the target state has a limited number of repeats, this callback is called when the animation finishes.
   * @param {boolean} [doSwitchIfTransitioning=false] if true and the layer is currently transition, switch immediately to the given state. Otherwise the transition is ignored and false is returned.
   * @returns {boolean} true if a transition was found and started, or if current state was switched while already in a transition (see doSwitchIfTransitioning)
   */
  transitionTo(state, globalTime, finishCallback, doSwitchIfTransitioning) {
    doSwitchIfTransitioning = !!doSwitchIfTransitioning;  // defaults to false if doSwitchIfTransitioning is undefined
    globalTime = typeof globalTime !== 'undefined' ? globalTime : this.getWorldTime();
    const cState = this._currentState;
    let transition;
    if (this._steadyStates[state] === cState) {
      return false;
    }
    if (!this._steadyStates[state]) {
      return false;
    }

    if (cState && cState._transitions) {
      transition = cState._transitions[state] || cState._transitions['*'];
    }
    if (!transition && this._transitions) {
      transition = this._transitions[state] || this._transitions['*'];
    }
    if (cState instanceof SteadyState && transition) {
      var transitionState = this._getTransitionByType(transition.type);
      this._doTransition(transitionState, cState, this._steadyStates[state], transition, globalTime, finishCallback);
      return true;
    } else if (!cState && transition) {
        var transitionState = this._getTransitionByType(transition.type);
        if (transitionState) {
          this._doTransition(transitionState, null, this._steadyStates[state], transition, globalTime, finishCallback);
          return true;
        }
    }
    // if we're here, it means we're currently in a transition
    if(doSwitchIfTransitioning) {
      this.setCurrentState(this._steadyStates[state], true, undefined, finishCallback);
      return true;
    }

    return false;
  }

  _doTransition(transition, source, target, config, globalTime, finishCallback) {
    if (source) {
      transition._sourceState = source;
      const timeWindow = config.timeWindow || [-1, -1];
      if (!transition.isValid(timeWindow, globalTime)) {
        console.warn('[Sumerian] State not in allowed time window');
        return;
      }
      source.onFinished = null;
    }
    transition._targetState = target;
    transition.readFromConfig(config);

    this.setCurrentState(transition, true, globalTime, finishCallback);
  }

  /**
   * Sets the current state to the given state. Generally for transitional state use.
   * @param {AbstractState} state our new state. If null, then no state is currently set on this layer.
   * @param {boolean} [rewind=false] if true, the clip(s) in the given state will be rewound by setting its start time to the current time and setting it active.
   * @param {number} [globalTime=this._world.time] start time for the transition, defaults to current time
   * @param {Function} [finishCallback] If the target state has a limited number of repeats, this callback is called when the animation finishes.
   */
  setCurrentState(state, rewind, globalTime, finishCallback) {
    globalTime = typeof globalTime !== 'undefined' ? globalTime : this.getWorldTime();
    this._currentState = state;
    if (state) {
      if (rewind) {
        state.resetClips(globalTime);
      }
      state.onFinished = function () {
        this.setCurrentState(state._targetState || null, false, undefined, finishCallback);
        if (state instanceof SteadyState && finishCallback instanceof Function) {
          finishCallback();
        }
        this.update();
      }.bind(this);
    }
  }

  /**
   * Get the current state
   * @returns {AbstractState}
   */
  getCurrentState() {
    return this._currentState;
  }

  /**
   * Set the current state by state id.
   * @param {string} id
   * @param {boolean} [rewind=false] if true, the clip(s) in the given state will be rewound by setting its start time to the current time and setting it active.
   * @param {number} [globalTime=this._world.time] start time for the transition, defaults to current time
   * @param {Function} callback If the target state has a limited number of repeats, this callback is called when the animation finishes.
   */
  setCurrentStateById(id, rewind, globalTime, callback) {
    const state = this.getStateById(id);
    this.setCurrentState(state, rewind, globalTime, callback);
  }

  /**
   * Get the current state by id.
   * @param {string} id
   * @returns {AbstractState}
   */
  getStateById(id) {
    return this._steadyStates[id];
  }

  /**
   * Get the current state by name.
   * @param {string} name
   * @returns {AbstractState}
   */
  getStateByName(name) {
    for (const id in this._steadyStates) {
      const state = this._steadyStates[id];
      if (state._name === name) {
        return this._steadyStates[id];
      }
    }
  }

  /**
   * Force the current state of the machine to the state with the given name.
   * @param {AbstractState} stateName the name of our state. If null, or is not present in this state machine, the current state is not changed.
   * @param {boolean} rewind if true, the clip(s) in the given state will be rewound by setting its start time to the current time and setting it active.
   * @param {number} [globalTime=this._world.time] start time for the transition, defaults to current time
   * @returns {boolean} true if succeeds
   */
  setCurrentStateByName(stateName, rewind, globalTime) {
    if (stateName) {
      const state = this.getStateByName(stateName);
      if (state) {
        this.setCurrentState(state, rewind, globalTime);
        return true;
      }
      console.warn(`[Sumerian] unable to find SteadyState named: ${stateName}`);
    }
    return false;
  }

  /**
   * @returns a source data mapping for the channels involved in the current state/transition of this layer.
   */
  getCurrentSourceData() {
    if (this._layerBlender) {
      return this._layerBlender.getBlendedSourceData();
    }

    if (this._currentState) {
      return this._currentState.getCurrentSourceData();
    }
    return null;
  }

  /**
   * Update the layer blender in this animation layer to properly point to the previous layer.
   * @param {Object} previousLayer the layer before this layer in the animation manager.
   * @private
   */
  updateLayerBlending(previousLayer) {
    if (this._layerBlender) {
      this._layerBlender._layerA = previousLayer;
      this._layerBlender._layerB = this;
    }
  }

  /**
   * Set the currently playing state on this layer to null.
   */
  clearCurrentState() {
    this.setCurrentState(null);
  }

  resetClips(globalTime) {
    if (this._currentState) {
      this._currentState.resetClips(typeof globalTime !== 'undefined' ? globalTime : this.getWorldTime());
    }
  }

  shiftClipTime(shiftTime) {
    if (this._currentState) {
      this._currentState.shiftClipTime(typeof shiftTime !== 'undefined' ? shiftTime : 0);
    }
  }

  setTimeScale(timeScale) {
    if (this._currentState) {
      this._currentState.setTimeScale(timeScale);
    }
  }

  _getTransitionByType(type) {
    if (this._transitionStates[type]) { return this._transitionStates[type]; }
    let transition;
    switch (type) {
      case 'Fade':
        transition = new FadeTransitionState();
        break;
      case 'SyncFade':
        transition = new SyncFadeTransitionState();
        break;
      case 'Frozen':
        transition = new FrozenTransitionState();
        break;
      default:
        console.log('[Sumerian] Defaulting to frozen transition type');
        transition = new FrozenTransitionState();
    }
    return this._transitionStates[type] = transition;
  }

  /**
   * @returns {AnimationLayer}
   */
  clone() {
    const cloned = new AnimationLayer(this._name, undefined, this.entity ? this.entity.world : null);


    for (var key in this._steadyStates) {
      cloned._steadyStates[key] = this._steadyStates[key].clone();
      if (this._steadyStates[key] === this._currentState) {
        cloned._currentState = cloned._steadyStates[key];
      }
    }

    for (var key in this._transitions) {
      cloned._transitions[key] = this._transitions[key];
    }

    for (var key in this._transitionStates) {
      cloned._transitionStates[key] = new this._transitionStates[key].constructor();
    }

    return cloned;
  }
}

AnimationLayer.BASE_LAYER_NAME = '-BASE_LAYER-';
AnimationLayer.DEFAULT_LAYER_TYPE = 'Override';

export default AnimationLayer;
